import classNames from "classnames/bind";
import { get } from "es-toolkit/compat";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Checkbox } from "../../Checkbox";
import { Skeleton } from "../../Skeleton";
import type { SortingType } from "../../utils/component-props";
import type {
  HeaderGroupRowProps,
  HeaderRowProps,
  HeaderType,
  ItemBaseType,
} from "../types";
import HeaderCell from "./HeaderCell/HeaderCell";
import styles from "./HeaderRow.module.scss";

const cx = classNames.bind(styles);

export const HeaderRow = <ItemType extends ItemBaseType>({
  headerGroups,
  ...rest
}: HeaderRowProps<ItemType>) => {
  return (
    <>
      {headerGroups.map((group, groupIdx) => (
        <HeaderGroupRow<ItemType>
          key={group.key}
          headerGroups={headerGroups}
          group={group}
          groupIdx={groupIdx}
          {...rest}
        />
      ))}
    </>
  );
};

const HeaderGroupRow = <ItemType extends ItemBaseType>({
  headerGroups,
  group,
  groupIdx,
  isLoading,
  isSticky,
  isScrolledVertically,
  showRowNumbers,
  onSelectAll,
  selectedStatus,
  isSelectionEnabled,
  isSingleSelection,
  hideSelectionCheckboxes,
  onSortChange,
  onHeaderClick,
  hasRowActions,
  tableRowActionsMaxWidth,
  onLastFixedCellPositionChange,
  scrollLeftAffordancePosition,
  scrollRightAffordancePosition,
  handleScrollAffordanceClick,
  shouldLeafCellsBreakMaxWidthCeiling,
  sorting,
  headers,
  items,
  density,
  intent,
  selectionBar,
}: HeaderGroupRowProps<ItemType>) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const selectionCheckboxCellRef = useRef<HTMLTableCellElement>(null);
  const [topPosition, setTopPosition] = useState(0);

  const toggleHeaderSort = (
    sortBy: SortingType["sortBy"],
    currentSortBy?: SortingType["sortBy"],
    currentSortDirection?: SortingType["sortDirection"]
  ): SortingType => {
    if (sortBy === currentSortBy) {
      return {
        sortBy,
        sortDirection: currentSortDirection === "asc" ? "desc" : "asc",
      };
    } else {
      return { sortBy, sortDirection: "asc" };
    }
  };

  const handleHeaderClick = useCallback(
    (dataKeys: string[]) => {
      onHeaderClick?.(
        items?.reduce<Array<Record<string, ItemType[keyof ItemType]>>>(
          (rowsAndDataForHeader, currentRow) => {
            return [
              ...rowsAndDataForHeader,
              dataKeys.reduce(
                (dataForHeader, headerKey) => ({
                  ...dataForHeader,
                  [headerKey]: get(currentRow, headerKey),
                }),
                {}
              ),
            ];
          },
          []
        )
      );
    },
    [items, onHeaderClick]
  );

  const handleHeaderCellClick = useCallback(
    (
      headerKey: string,
      columnDetails: {
        isSortable?: boolean;
        childColumns?: Array<HeaderType<ItemType>>;
      }
    ) => {
      handleHeaderClick?.(
        columnDetails.childColumns
          ? columnDetails.childColumns.map((c) => c.key)
          : [headerKey]
      );

      if (columnDetails.isSortable) {
        onSortChange?.(
          toggleHeaderSort(headerKey, sorting.sortBy, sorting.sortDirection)
        );
      }
    },
    [sorting]
  );

  // fix each header row at the right position from the top, accounting for sibling rows
  useEffect(() => {
    if (!isSticky || !rowRef.current?.parentNode) return;

    let newTopPos = 0;

    for (const node of Array.from(rowRef.current.parentNode.childNodes)) {
      if (node === rowRef.current || node.nodeType !== 1) {
        break;
      }

      newTopPos += (node as HTMLTableRowElement).clientHeight;
    }

    setTopPosition(newTopPos);
  }, [headerGroups, items, isSticky]);

  return (
    <tr
      className={cx(
        "headerRow",
        groupIdx < headerGroups.length - 1 &&
          headerGroups.length > 1 &&
          "isGroup",
        isSticky && "isSticky",
        isScrolledVertically && "hasContentUnder"
      )}
      style={{ ...(isSticky ? { top: topPosition } : {}) }}
      ref={rowRef}
    >
      {groupIdx === headerGroups.length - 1 &&
      ((!hideSelectionCheckboxes && isSelectionEnabled) || showRowNumbers) ? (
        <th
          className={cx(
            "cell",
            "cell--header",
            "isSelection",
            "sticky",
            "sticky--default",
            group.columns[0] && !group.columns[0].isFixed && "sticky--last"
          )}
          tabIndex={0}
          data-role="data-table-row-indicator"
          ref={selectionCheckboxCellRef}
        >
          {!hideSelectionCheckboxes &&
          !isSingleSelection &&
          isSelectionEnabled ? (
            <div className={cx("selection-cell-wrapper")}>
              <Skeleton isLoading={isLoading}>
                <Checkbox
                  name="datatable-are-all-selected-checkbox"
                  onChange={onSelectAll}
                  {...selectedStatus}
                />
              </Skeleton>
            </div>
          ) : null}
        </th>
      ) : null}
      {selectionBar && groupIdx === headerGroups.length - 1 ? (
        <th
          colSpan={group.columns.length}
          style={{ position: "relative" }}
        >
          {selectionBar(selectionCheckboxCellRef.current?.clientWidth ?? 0)}
        </th>
      ) : (
        group.columns.map((column, columnIdx) => {
          const handleClick = () =>
            handleHeaderCellClick(column.key, {
              isSortable: column.isSortable,
              childColumns: group.columns[columnIdx]?.headers,
            });

          const isLastRow = groupIdx === headerGroups.length - 1;
          const isFirstCell = columnIdx === 0;
          const isLastCell = columnIdx === group.columns.length - 1;
          const isPreviousCellFixed =
            columnIdx - 1 >= 0 && group.columns[columnIdx - 1].isFixed;
          const isNextCellFixed =
            columnIdx + 1 < group.columns.length - 1 &&
            group.columns[columnIdx + 1].isFixed;
          const actualScrollLeftAffordancePosition =
            isLastRow &&
            ((isFirstCell && !column.isFixed) ||
              (isPreviousCellFixed && !column.isFixed))
              ? scrollLeftAffordancePosition
              : null;
          const actualScrollRightAffordancePosition =
            isLastRow &&
            ((isLastCell && !column.isFixed) ||
              (isNextCellFixed && !column.isFixed))
              ? scrollRightAffordancePosition
              : null;
          const style: CSSProperties = {
            ...(hasRowActions && columnIdx === group.columns.length - 1
              ? { paddingRight: tableRowActionsMaxWidth }
              : {}),
          };

          return (
            <HeaderCell
              key={column.key}
              style={style}
              colSpan={column.span}
              alignment={column.alignment}
              isScrolledVertically={isScrolledVertically}
              isSticky={isSticky}
              isGroup={
                groupIdx < headerGroups.length - 1 && headerGroups.length > 1
              }
              headerKey={column.key}
              label={column.label}
              tooltip={column.tooltip}
              isLoading={isLoading}
              isFixed={column.isFixed}
              isDefaultFixed={column.isDefaultFixed}
              isLastFixedCell={
                column.isFixed &&
                group.columns[columnIdx + 1] &&
                !group.columns[columnIdx + 1].isFixed
              }
              onLastFixedCellPositionChange={onLastFixedCellPositionChange}
              scrollLeftAffordancePosition={actualScrollLeftAffordancePosition}
              scrollRightAffordancePosition={
                actualScrollRightAffordancePosition
              }
              handleScrollAffordanceClick={handleScrollAffordanceClick}
              shouldLeafCellsBreakMaxWidthCeiling={
                shouldLeafCellsBreakMaxWidthCeiling
              }
              isSortable={column.isSortable}
              onClick={handleClick}
              sorting={sorting}
              headers={headers}
              items={items}
              density={density}
              intent={intent}
            />
          );
        })
      )}
    </tr>
  );
};

HeaderRow.HeaderCell = HeaderCell;
